import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, Input } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter'
import { TwoFactorSetup } from '@/components/TwoFactorSetup'
import { apiErrorMessage } from '@/lib/api-error'
import { authApi } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/authStore'

export function Security() {
  const { t } = useTranslation('auth')
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState(false)
  const [loading, setLoading] = useState(false)

  async function refreshUser() {
    try {
      const r = await authApi.me()
      setUser(r.user)
    } catch {
      /* ignore — store keeps last known value */
    }
  }

  async function changePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setOk(false)
    setLoading(true)
    try {
      await authApi.changePassword(current, next)
      setOk(true)
      setCurrent('')
      setNext('')
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-8">
      <header>
        <h1 className="text-2xl font-semibold">{t('security.title')}</h1>
        <p className="mt-1 text-text-muted">{t('security.subtitle')}</p>
      </header>

      <section>
        <h2 className="mb-3 text-lg font-medium">{t('security.password_section')}</h2>
        <Card>
          <form className="flex flex-col gap-4" onSubmit={changePassword}>
            <Input
              label={t('security.current_password')}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
            <div>
              <Input
                label={t('security.new_password')}
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={12}
              />
              <PasswordStrengthMeter password={next} />
            </div>
            {error ? <ErrorBanner message={error} /> : null}
            {ok ? <p className="text-sm text-success">{t('security.password_changed')}</p> : null}
            <Button type="submit" disabled={loading}>
              {t('security.change_password')}
            </Button>
          </form>
        </Card>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-medium">{t('security.twofa_section')}</h2>
        <TwoFactorSetup enabled={user?.two_factor_enabled ?? false} onChange={refreshUser} />
      </section>
    </div>
  )
}
